/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 */

import {
  BuildingAmenity,
  type EnumTitle,
  ListingAmenity,
  getEnumTitle,
  getEnumValues,
} from '@zcode/enums'
import { isZumper } from '@zcode/env'
import type { Filters } from '@zcode/state-type'
import { logger } from '@zcode/utils'

export const displayAmenity = (value: ListingAmenity) => {
  try {
    const title = getEnumTitle(ListingAmenity, value)
    return title
  } catch (error) {
    logger.error(error)
    return ''
  }
}

export const displayBuildingAmenity = (value: BuildingAmenity) => {
  try {
    const title = getEnumTitle(BuildingAmenity, value)
    return title
  } catch (error) {
    logger.error(error)
    return ''
  }
}

/**
 * Combines all the listing amenities and building amenities in a singular data structure. An
 * entry will have the following schema
 * {
     title, // title of an amenity
     key, // unique key of an amenity
     isListingAmenity, // whether listing or building amenity
 * }
 */
export const getAllAmenities = (fromFilters?: boolean) => {
  const listingAmenities = getAllListingAmenities()
  const buildingAmenities = getAllBuildingAmenities(fromFilters)
  // handle duplicate amenity across building and listing
  const dups = new Set<(typeof listingAmenities | typeof buildingAmenities)[number]['title']>(
    listingAmenities.map(({ title }) => title)
  )
  return [
    ...listingAmenities,
    ...buildingAmenities.map((entry) => {
      if (dups.has(entry.title)) {
        return { ...entry, title: `${entry.title} - Building` as const }
      }
      return entry
    }),
  ].sort((a, b) => {
    return a.title.localeCompare(b.title)
  })
}

const listingAmenitiesSTR: readonly ListingAmenity[] = [
  ListingAmenity.EV_CHARGING,
  ListingAmenity.WIFI,
  ListingAmenity.TV,
  ListingAmenity.CABLE,
  ListingAmenity.OVEN,
  ListingAmenity.MICROWAVE,
]

function isntUndefined<T>(anything: T | undefined): anything is T {
  return typeof anything !== 'undefined'
}

const getAllListingAmenities = () => {
  return getEnumValues(ListingAmenity)
    .map((key) => {
      return key !== 0 && (key < ListingAmenity.LEED_CERTIFIED || listingAmenitiesSTR.includes(key))
        ? getAmenitiesHelper<typeof ListingAmenity, typeof key>(
            getEnumTitle(ListingAmenity, key),
            key,
            true
          )
        : undefined
    })
    .filter(isntUndefined)
}

const getAllBuildingAmenities = (fromFilters?: boolean) => {
  return getEnumValues(BuildingAmenity)
    .map((key) => {
      if (fromFilters && key === BuildingAmenity.ON_SITE_LAUNDRY) {
        return
      }

      return key !== 0 && key <= BuildingAmenity.LEED_CERTIFIED
        ? getAmenitiesHelper<typeof BuildingAmenity, typeof key>(
            getEnumTitle(BuildingAmenity, key),
            key,
            false
          )
        : undefined
    })
    .filter(isntUndefined)
}

const CONVERTED_AMENITIY_TITLES = {
  [getEnumTitle(BuildingAmenity, BuildingAmenity.FITNESS_CENTER)]: 'Gym',
  [getEnumTitle(BuildingAmenity, BuildingAmenity.ON_SITE_LAUNDRY)]: 'Laundry: Building',
  [getEnumTitle(BuildingAmenity, BuildingAmenity.SWIMMING_POOL)]: 'Pool',
  [getEnumTitle(ListingAmenity, ListingAmenity.IN_UNIT_LAUNDRY)]: 'Laundry: In-unit',
} as const

function isIn<Obj extends object, Key extends string>(key: Key, obj: Obj): key is Key & keyof Obj {
  return key in obj
}

function getAmenitiesHelper<
  Enum extends typeof BuildingAmenity | typeof ListingAmenity,
  Key extends Enum[keyof Enum] & number,
>(
  title: EnumTitle<Enum, Key>,
  key: Key,
  isListingAmenity: Enum extends typeof ListingAmenity ? true : false
) {
  // TODO: BE should update generated enums to have correct capitalization. This is a quick fix

  if (isZumper) {
    return {
      title: isIn(title, CONVERTED_AMENITIY_TITLES) ? CONVERTED_AMENITIY_TITLES[title] : title,
      key,
      isListingAmenity,
    }
  }
  return {
    title: title === 'Leed Certified' ? ('LEED Certified' as const) : title,
    key,
    isListingAmenity,
  }
}

/**
 * Constructs a list that contain a corresponding listing/building amenity title to the given
 * listingAmenities/buildingAmenities ids
 * e.g. ([1], [3], 'customKeyword') => ['On site Laundry', 'Fitness Center', 'customKeyword']
 * @param listingAmenities contains selected listing amenity ids
 * @param buildingAmenities contains selected building amenity ids
 * @param keywords keywords separated by comma
 */
export const getSelectedAmenityTitle = <
  ListingAmenities extends Filters['listingAmenities'] | null,
  BuildingAmenities extends Filters['buildingAmenities'] | null,
  Keywords extends string | null,
>(
  listingAmenities: ListingAmenities,
  buildingAmenities: BuildingAmenities,
  keywords: Keywords
): Array<
  | Exclude<Keywords, null | ''>
  | (BuildingAmenities extends null ? never : EnumTitle<typeof BuildingAmenity, BuildingAmenity>)
  | (ListingAmenities extends null ? never : EnumTitle<typeof ListingAmenity, ListingAmenity>)
> => {
  const titleSet: Set<
    | Exclude<Keywords, null | ''>
    | (BuildingAmenities extends null ? never : EnumTitle<typeof BuildingAmenity, BuildingAmenity>)
    | (ListingAmenities extends null ? never : EnumTitle<typeof ListingAmenity, ListingAmenity>)
  > = new Set()
  if (Array.isArray(listingAmenities)) {
    for (const id of listingAmenities) {
      // @ts-expect-error TS can't tell that the `extends null` in the types matches up with the `Array.isArray` check
      titleSet.add(getEnumTitle(ListingAmenity, id))
    }
  }
  if (Array.isArray(buildingAmenities)) {
    for (const id of buildingAmenities) {
      // @ts-expect-error TS can't tell that the `extends null` in the types matches up with the `Array.isArray` check
      titleSet.add(getEnumTitle(BuildingAmenity, id))
    }
  }
  if (keywords) {
    // @ts-expect-error TS can't tell that the if statement means `keywords` isn't an empty string
    titleSet.add(keywords)
  }
  return Array.from(titleSet)
}
